import React, { useState, useEffect, useRef } from 'react';
import { DashboardWidget } from './DashboardWidget';
import { TableWidget, TableRow } from './TableWidget';
import { fetchTokenPrice } from '../../api/userPositions';
import { useConfig } from '../../hooks/useConfig';
import { TOKENS } from '@sepori/shared/registry';

interface TokenPricesWidgetProps {
  className?: string;
}

export const TokenPricesWidget: React.FC<TokenPricesWidgetProps> = ({ className }) => {
  const { widgetAutoRefreshIntervalMs } = useConfig();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<TableRow[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Define the tokens we want to display with custom names and ordering
  const tokenConfig = [
    { symbol: 'CBBTC', displayName: 'Bitcoin (cbBTC)' },
    { symbol: 'WETH', displayName: 'Ethereum (WETH)' },
    { symbol: 'AERO', displayName: 'Aero (AERO)' },
    { symbol: 'KVCM', displayName: 'Klima (kVCM)' }
  ];

  const loadData = async (bypassCache = false) => {
    try {
      setLoading(true);
      setError(null);

      const newRows: TableRow[] = [];

      for (const config of tokenConfig) {
        try {
          // Get token metadata from registry
          const token = TOKENS[config.symbol as keyof typeof TOKENS];
          if (!token) continue;

          // Fetch current price
          const priceData = await fetchTokenPrice(token.address, { bypassCache });
          const price = priceData.priceUsd;

          newRows.push({
            token: config.displayName,
            value: `$${price.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}`
          });
        } catch (err) {
          console.warn(`Failed to fetch price for ${config.symbol}:`, err);
          // Add error row for this token
          newRows.push({
            token: config.displayName,
            value: 'Error'
          });
        }
      }

      setRows(newRows);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to load token prices:', err);
      setError(err instanceof Error ? err.message : 'Failed to load token prices');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    try {
      await loadData(true); // bypassCache=true for manual refresh
    } catch (err) {
      console.error('Failed to refresh token prices:', err);
    } finally {
      setRefreshing(false);
    }
  };

  // Auto-refresh every 5 minutes
  useEffect(() => {
    // Initial load
    loadData();

    // Set up auto-refresh interval
    intervalRef.current = setInterval(() => {
      loadData();
    }, widgetAutoRefreshIntervalMs); // Configurable interval (default 5 minutes)

    // Cleanup interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div className={`dashboard-widget token-prices ${className}`}>
        <div className="widget-header">
          <h3 className="widget-title">Current Prices</h3>
        </div>
        <div className="widget-content">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading prices...</p>
          </div>
        </div>
        <div className="widget-refresh-bottom">
          <button
            className="refresh-button"
            disabled={true}
            style={{ pointerEvents: 'none' }}
          >
            Refresh
          </button>
          {lastUpdated && (
            <span className="last-updated">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <DashboardWidget
      title="Current Prices"
      loading={false}
      error={error || undefined}
      className={`token-prices ${className}`}
      onRefresh={loading ? undefined : refreshData}
      refreshing={refreshing || loading}
      lastUpdated={lastUpdated ?? undefined}
      refreshButtonBottom={true}
    >
      <TableWidget
        rows={rows}
        showLabelColumn={false}
        showTokenColumn={true}
        showAmountColumn={false}
        columnHeaders={{
          token: 'Token',
          value: 'Price'
        }}
      />
    </DashboardWidget>
  );
};
